import { useTranslation } from 'react-i18next'
import Card from '@/components/ui/Card'
import Input from '@/components/ui/Input'
import Button from '@/components/ui/Button'
import type { ZakatMoneyInput, CalendarType } from '@/types/zakat'
import { Wallet } from 'lucide-react'

interface ZakatMoneyFormProps {
  input: ZakatMoneyInput
  calendar: CalendarType
  onInputChange: (input: ZakatMoneyInput) => void
  onCalendarChange: (calendar: CalendarType) => void
  onCalculate: () => void
}

export default function ZakatMoneyForm({
  input,
  calendar,
  onInputChange,
  onCalendarChange,
  onCalculate,
}: ZakatMoneyFormProps) {
  const { t } = useTranslation()
  const handleInputChange = (field: keyof ZakatMoneyInput, value: string) => {
    const numValue = parseFloat(value) || 0
    onInputChange({
      ...input,
      [field]: numValue,
    })
  }

  return (
    <Card>
      <div className="flex items-center gap-3 mb-6">
        <Wallet className="w-6 h-6 text-primary-600 dark:text-primary-400" />
        <h3 className="text-2xl font-bold text-gray-900 dark:text-gray-100">{t('zakat.types.money')}</h3>
      </div>

      <div className="mb-6">
        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
          {t('zakat.yearOfHawl')}
        </label>
        <div className="flex gap-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="calendar"
              value="hijri"
              checked={calendar === 'hijri'}
              onChange={() => onCalendarChange('hijri')}
              className="w-4 h-4 text-primary-600 focus:ring-primary-500"
            />
            <span className="text-gray-700 dark:text-gray-300">{t('zakat.hijri')}</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="calendar"
              value="gregorian"
              checked={calendar === 'gregorian'}
              onChange={() => onCalendarChange('gregorian')}
              className="w-4 h-4 text-primary-600 focus:ring-primary-500"
            />
            <span className="text-gray-700 dark:text-gray-300">{t('zakat.gregorian')}</span>
          </label>
        </div>
      </div>

      <div className="space-y-4 mb-6">
        <Input
          type="number"
          label={t('zakat.financialAmount')}
          value={input.amount.toString()}
          onChange={(e) => handleInputChange('amount', e.target.value)}
          placeholder={t('zakat.placeholderAmount')}
        />

        <Input
          type="number"
          label={t('zakat.goldPricePerGram24')}
          value={input.goldPricePerGram24.toString()}
          onChange={(e) => handleInputChange('goldPricePerGram24', e.target.value)}
          placeholder={t('zakat.placeholderGoldPrice')}
        />
      </div>

      <a
        href="https://dahabmasr.com/"
        target="_blank"
        rel="noopener noreferrer"
        className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 text-sm mb-4 block"
      >
        {t('zakat.knowGoldPrice')}
      </a>

      <Button variant="primary" size="lg" className="w-full" onClick={onCalculate}>
        {t('zakat.calculateYourZakat')}
      </Button>
    </Card>
  )
}

